import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { SearchableSelect } from '../ui/searchable-select';
import { Plus, Trash2 } from 'lucide-react';

const VEHICLE_TYPE_OPTIONS = [
  {
    label: 'Standard Dry / Closed Vehicles',
    options: [
      'Pickup Van (0.5 - 1 Ton)',
      'Small Box Truck (1 - 2 Ton)',
      'Medium Box Truck (3 - 5 Ton)',
      'Large Box Truck (7 - 10 Ton)',
      'Heavy Box Truck (15 - 16 Ton)',
      'Curtain Side Truck',
      'Tautliner Trailer',
    ],
  },
  {
    label: 'Container Trailers (Port / ICD / Cross Border)',
    options: [
      '20 ft Container Trailer',
      '40 ft Container Trailer',
      '40 ft High Cube Trailer',
      '45 ft Container Trailer',
    ],
  },
  {
    label: 'Open / Flatbed Vehicles',
    options: [
      'Open Truck (10 Ton)',
      'Open Truck (16 Ton)',
      'Open Truck (25 Ton)',
      'Flatbed Trailer',
      'Semi Low Bed Trailer',
    ],
  },
  {
    label: 'Project / ODC Equipment',
    options: [
      'Low Bed Trailer',
      'Hydraulic Axle Trailer',
      'Multi Axle Modular Trailer',
      'Extendable Trailer',
      'Girder Bridge Trailer',
    ],
  },
  {
    label: 'Temperature Controlled',
    options: [
      'Refrigerated Truck Small (2-5 Ton)',
      'Refrigerated Truck Medium (6-10 Ton)',
      'Refrigerated Trailer 20 ft',
      'Refrigerated Trailer 40 ft',
    ],
  },
  {
    label: 'Specialized Equipment',
    options: [
      'Tanker Trailer (Liquid / Chemical / Fuel)',
      'Tipper Truck (Bulk Cargo)',
      'Car Carrier Trailer',
      'Livestock Carrier',
      'Scrap Compactor Truck',
    ],
  },
];

const TRANSPORT_COMMODITY_OPTIONS = [
  'General Cargo',
  'FMCG / Retail Goods',
  'Electronics / High Value Cargo',
  'Pharmaceuticals',
  'Food & Perishables',
  'Chemicals - Non Hazardous',
  'Hazardous Cargo (DG)',
  'Machinery / Industrial Equipment',
  'Automobile Parts',
  'Steel / Metal Products',
  'Construction Materials',
  'Project Cargo / ODC',
  'Liquid Bulk',
  'Scrap / Waste Cargo',
  'E-commerce Shipments',
  'Agricultural Produce',
];

const SUB_VEHICLE_OPTIONS_BY_TYPE = VEHICLE_TYPE_OPTIONS.reduce((acc, group) => {
  acc[group.label] = group.options;
  return acc;
}, {});

const LEGACY_VEHICLE_TYPE_MAP = {
  Truck: { vehicleType: 'Standard Dry / Closed Vehicles' },
  Van: { vehicleType: 'Standard Dry / Closed Vehicles', subVehicleType: 'Pickup Van (0.5 - 1 Ton)' },
  Flatbed: { vehicleType: 'Open / Flatbed Vehicles', subVehicleType: 'Flatbed Trailer' },
  'Refrigerated Truck': { vehicleType: 'Temperature Controlled' },
  Tanker: { vehicleType: 'Specialized Equipment', subVehicleType: 'Tanker Trailer (Liquid / Chemical / Fuel)' },
  'Container Truck': { vehicleType: 'Container Trailers (Port / ICD / Cross Border)' },
};

const DEFAULT_LOAD_TYPE = 'FTL (Full Truck Load)';
const DIMENSION_UNIT_OPTIONS = [
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'inch', label: 'Inch' },
  { value: 'm', label: 'Meter' },
];

const emptyCargoItem = () => ({
  vehicleType: '',
  subVehicleType: '',
  loadType: DEFAULT_LOAD_TYPE,
  commodity: '',
  cargoWeight: '',
  cargoVolume: '',
  dimensions: { length: '', width: '', height: '', unit: 'cm' }
});

const DEFAULT_TRANSPORT_FORM = {
  serviceType: 'Transport',
  scope: [],
  pickupLocation: { address: '', city: '', country: '', postalCode: '' },
  deliveryLocation: { address: '', city: '', country: '', postalCode: '' },
  pickupDate: '',
  deliveryDate: '',
  vehicleType: '',
  subVehicleType: '',
  loadType: DEFAULT_LOAD_TYPE,
  commodity: '',
  cargoWeight: '',
  cargoVolume: '',
  dimensions: { length: '', width: '', height: '', unit: 'cm' },
  cargoDetails: [],
  requiresCustomsClearance: false,
  customsDetails: { exportCountry: '', importCountry: '', hsCode: '', customsValue: '' },
  specialRequirements: [],
  temperatureControl: false,
  tempRange: '',
  insuranceRequired: false,
  insuranceValue: '',
  notes: ''
};

const normalizeCargoItem = (item = {}) => {
  const legacyVehicle = LEGACY_VEHICLE_TYPE_MAP[item.vehicleType] || {};
  const normalizedVehicleType = item.vehicleType && SUB_VEHICLE_OPTIONS_BY_TYPE[item.vehicleType]
    ? item.vehicleType
    : (legacyVehicle.vehicleType || '');
  const validSubTypes = SUB_VEHICLE_OPTIONS_BY_TYPE[normalizedVehicleType] || [];
  const requestedSubVehicleType = item.subVehicleType || legacyVehicle.subVehicleType || '';
  const normalizedSubVehicleType = validSubTypes.includes(requestedSubVehicleType) ? requestedSubVehicleType : '';

  return {
    ...emptyCargoItem(),
    ...item,
    vehicleType: normalizedVehicleType,
    subVehicleType: normalizedSubVehicleType,
    dimensions: item.dimensions ? { ...item.dimensions, unit: item.dimensions.unit || 'cm' } : { length: '', width: '', height: '', unit: 'cm' },
  };
};

const TransportForm = ({ onSubmit, onCancel, commodities = [], initialData = null, formId = null, scopeOptions: scopeOptionsProp = [], onFormDataChange, validationErrors = {}, clearValidationError }) => {
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState(() => {
    const base = { ...DEFAULT_TRANSPORT_FORM };
    if (initialData && typeof initialData === 'object') {
      Object.assign(base, initialData);
      if (!base.cargoDetails || base.cargoDetails.length === 0) {
        if (base.vehicleType || base.commodity || base.cargoWeight) {
          base.cargoDetails = [normalizeCargoItem({
            vehicleType: base.vehicleType || '',
            subVehicleType: base.subVehicleType || '',
            loadType: base.loadType || DEFAULT_LOAD_TYPE,
            commodity: base.commodity || '',
            cargoWeight: base.cargoWeight || '',
            cargoVolume: base.cargoVolume || '',
            dimensions: base.dimensions,
          })];
        } else {
          base.cargoDetails = [emptyCargoItem()];
        }
      }
      base.cargoDetails = base.cargoDetails.map((item) => normalizeCargoItem(item));
      return base;
    }
    base.cargoDetails = [emptyCargoItem()];
    return base;
  });

  const loadTypes = [
    'FTL (Full Truck Load)',
    'LTL (Less Than Truck Load)',
    'PTL (Part Truck Load)',
    'Express Dedicated Vehicle',
    'Milk Run Pickup',
    'Line Haul Movement',
    'Last Mile Delivery',
  ];
  const specialReqs = ['Liftgate Required', 'Inside Delivery', 'Residential Delivery', 'Appointment Required', 'Hazardous Material'];
  const scopeOptions = scopeOptionsProp && scopeOptionsProp.length > 0 ? scopeOptionsProp : [];

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  const addCargoItem = () => {
    setFormData((prev) => ({ ...prev, cargoDetails: [...(prev.cargoDetails || []), emptyCargoItem()] }));
  };

  const removeCargoItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      cargoDetails: (prev.cargoDetails || []).filter((_, i) => i !== index)
    }));
  };

  const updateCargoItem = (index, field, value) => {
    setFormData((prev) => {
      const list = [...(prev.cargoDetails || [])];
      if (!list[index]) list[index] = emptyCargoItem();
      if (field.startsWith('dimensions.')) {
        const dimField = field.split('.')[1];
        list[index] = { ...list[index], dimensions: { ...(list[index].dimensions || {}), [dimField]: value } };
      } else if (field === 'vehicleType') {
        list[index] = { ...list[index], vehicleType: value, subVehicleType: '' };
      } else {
        list[index] = { ...list[index], [field]: value };
      }
      return { ...prev, cargoDetails: list };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...formData };
    const first = (payload.cargoDetails && payload.cargoDetails[0]) || emptyCargoItem();
    payload.vehicleType = first.vehicleType;
    payload.subVehicleType = first.subVehicleType;
    payload.loadType = first.loadType;
    payload.commodity = first.commodity;
    payload.cargoWeight = first.cargoWeight;
    payload.cargoVolume = first.cargoVolume;
    payload.dimensions = first.dimensions ? { ...first.dimensions, unit: first.dimensions.unit || 'cm' } : { length: '', width: '', height: '', unit: 'cm' };
    onSubmit(payload);
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Service Type */}
      <div>
        <Label>Service Type</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <label className="flex items-center space-x-2 p-3 border rounded cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              checked={formData.serviceType === 'Transport'}
              onChange={() => setFormData({ ...formData, serviceType: 'Transport' })}
            />
            <span className="text-sm font-medium">Transport Only</span>
          </label>
          <label className="flex items-center space-x-2 p-3 border rounded cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              checked={formData.serviceType === 'Transport + Customs'}
              onChange={() => setFormData({ ...formData, serviceType: 'Transport + Customs', requiresCustomsClearance: true })}
            />
            <span className="text-sm font-medium">Transport + Customs Clearance</span>
          </label>
        </div>
      </div>

      {scopeOptions.length > 0 && (
        <div>
          <Label>Scope (Select all that apply)</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            {scopeOptions.map((scope) => (
              <label key={scope} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={(formData.scope || []).includes(scope)}
                  onChange={() => {
                    const current = formData.scope || [];
                    const newScopes = current.includes(scope) ? current.filter(s => s !== scope) : [...current, scope];
                    setFormData({ ...formData, scope: newScopes });
                    onFormDataChange?.({ scope: newScopes });
                    clearValidationError?.('scope');
                  }}
                  className="rounded"
                />
                <span className="text-sm">{scope}</span>
              </label>
            ))}
          </div>
          {validationErrors.scope && <p className="mt-1 text-sm text-red-600">{validationErrors.scope}</p>}
        </div>
      )}

      {/* Pickup Location */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Pickup Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Address</Label>
            <Input
              value={formData.pickupLocation.address}
              onChange={(e) => setFormData({ ...formData, pickupLocation: { ...formData.pickupLocation, address: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.(['pickupLocation.address', 'pickupLocation.city']);
              }}
              placeholder="Street address"
              className={validationErrors['pickupLocation.address'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['pickupLocation.address'] && <p className="mt-1 text-sm text-red-600">{validationErrors['pickupLocation.address']}</p>}
          </div>
          <div>
            <Label>City</Label>
            <Input
              value={formData.pickupLocation.city}
              onChange={(e) => setFormData({ ...formData, pickupLocation: { ...formData.pickupLocation, city: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.(['pickupLocation.address', 'pickupLocation.city']);
              }}
              className={validationErrors['pickupLocation.city'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['pickupLocation.city'] && <p className="mt-1 text-sm text-red-600">{validationErrors['pickupLocation.city']}</p>}
          </div>
          <div>
            <Label>Country</Label>
            <Input
              value={formData.pickupLocation.country}
              onChange={(e) => setFormData({ ...formData, pickupLocation: { ...formData.pickupLocation, country: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.('pickupLocation.country');
              }}
              className={validationErrors['pickupLocation.country'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['pickupLocation.country'] && <p className="mt-1 text-sm text-red-600">{validationErrors['pickupLocation.country']}</p>}
          </div>
          <div>
            <Label>Postal Code</Label>
            <Input
              value={formData.pickupLocation.postalCode}
              onChange={(e) => setFormData({ ...formData, pickupLocation: { ...formData.pickupLocation, postalCode: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.('pickupLocation.postalCode');
              }}
              className={validationErrors['pickupLocation.postalCode'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['pickupLocation.postalCode'] && <p className="mt-1 text-sm text-red-600">{validationErrors['pickupLocation.postalCode']}</p>}
          </div>
          <div>
            <Label>Pickup Date</Label>
            <Input
              type="date"
              min={today}
              value={formData.pickupDate}
              onChange={(e) => setFormData({ ...formData, pickupDate: e.target.value })}
              onInput={(e) => {
                if (e.target.value) clearValidationError?.('pickupDate');
              }}
              className={validationErrors.pickupDate ? 'border-red-500' : ''}
              required
            />
            {validationErrors.pickupDate && <p className="mt-1 text-sm text-red-600">{validationErrors.pickupDate}</p>}
          </div>
        </div>
      </div>

      {/* Delivery Location */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Delivery Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Address</Label>
            <Input
              value={formData.deliveryLocation.address}
              onChange={(e) => setFormData({ ...formData, deliveryLocation: { ...formData.deliveryLocation, address: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.(['deliveryLocation.address', 'deliveryLocation.city']);
              }}
              placeholder="Street address"
              className={validationErrors['deliveryLocation.address'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['deliveryLocation.address'] && <p className="mt-1 text-sm text-red-600">{validationErrors['deliveryLocation.address']}</p>}
          </div>
          <div>
            <Label>City</Label>
            <Input
              value={formData.deliveryLocation.city}
              onChange={(e) => setFormData({ ...formData, deliveryLocation: { ...formData.deliveryLocation, city: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.(['deliveryLocation.address', 'deliveryLocation.city']);
              }}
              className={validationErrors['deliveryLocation.city'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['deliveryLocation.city'] && <p className="mt-1 text-sm text-red-600">{validationErrors['deliveryLocation.city']}</p>}
          </div>
          <div>
            <Label>Country</Label>
            <Input
              value={formData.deliveryLocation.country}
              onChange={(e) => setFormData({ ...formData, deliveryLocation: { ...formData.deliveryLocation, country: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.('deliveryLocation.country');
              }}
              className={validationErrors['deliveryLocation.country'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['deliveryLocation.country'] && <p className="mt-1 text-sm text-red-600">{validationErrors['deliveryLocation.country']}</p>}
          </div>
          <div>
            <Label>Postal Code</Label>
            <Input
              value={formData.deliveryLocation.postalCode}
              onChange={(e) => setFormData({ ...formData, deliveryLocation: { ...formData.deliveryLocation, postalCode: e.target.value } })}
              onInput={(e) => {
                if (e.target.value?.trim()) clearValidationError?.('deliveryLocation.postalCode');
              }}
              className={validationErrors['deliveryLocation.postalCode'] ? 'border-red-500' : ''}
              required
            />
            {validationErrors['deliveryLocation.postalCode'] && <p className="mt-1 text-sm text-red-600">{validationErrors['deliveryLocation.postalCode']}</p>}
          </div>
          <div>
            <Label>Delivery Date</Label>
              <Input
                type="date"
                min={today}
                value={formData.deliveryDate}
                onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
              onInput={(e) => {
                if (e.target.value) clearValidationError?.('deliveryDate');
              }}
              className={validationErrors.deliveryDate ? 'border-red-500' : ''}
              required
            />
            {validationErrors.deliveryDate && <p className="mt-1 text-sm text-red-600">{validationErrors.deliveryDate}</p>}
          </div>
        </div>
      </div>

      {/* Cargo Details */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">Cargo Details</h3>
          <Button type="button" variant="outline" size="sm" onClick={addCargoItem} className="flex items-center gap-1">
            <Plus size={14} />
            Add item
          </Button>
        </div>
        {validationErrors.cargoDetails && <p className="mb-3 text-sm text-red-600">{validationErrors.cargoDetails}</p>}
        {(formData.cargoDetails || []).map((item, index) => (
          <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
              {(formData.cargoDetails?.length || 0) > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCargoItem(index)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1"
                  aria-label="Remove item"
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Vehicle Type</Label>
                <SearchableSelect
                  options={VEHICLE_TYPE_OPTIONS.map((group) => ({ value: group.label, label: group.label }))}
                  value={item.vehicleType || ''}
                  onChange={(value) => {
                    updateCargoItem(index, 'vehicleType', value || '');
                    if (index === 0 && value) clearValidationError?.('cargoDetails.0.vehicleType');
                  }}
                  placeholder="Search or select vehicle type"
                  allowOther={false}
                  error={index === 0 && !!validationErrors['cargoDetails.0.vehicleType']}
                />
                {index === 0 && validationErrors['cargoDetails.0.vehicleType'] && <p className="mt-1 text-sm text-red-600">{validationErrors['cargoDetails.0.vehicleType']}</p>}
              </div>
              <div>
                <Label>Sub Vehicle Type</Label>
                <SearchableSelect
                  options={(SUB_VEHICLE_OPTIONS_BY_TYPE[item.vehicleType] || []).map((type) => ({ value: type, label: type }))}
                  value={item.subVehicleType || ''}
                  onChange={(value) => {
                    updateCargoItem(index, 'subVehicleType', value || '');
                    if (index === 0 && value) clearValidationError?.('cargoDetails.0.subVehicleType');
                  }}
                  placeholder={item.vehicleType ? 'Search or select sub vehicle type' : 'Select vehicle type first'}
                  disabled={!item.vehicleType}
                  allowOther={false}
                  error={index === 0 && !!validationErrors['cargoDetails.0.subVehicleType']}
                />
                {index === 0 && validationErrors['cargoDetails.0.subVehicleType'] && <p className="mt-1 text-sm text-red-600">{validationErrors['cargoDetails.0.subVehicleType']}</p>}
              </div>
              <div>
                <Label>Load Type</Label>
                <SearchableSelect
                  options={loadTypes.map((type) => ({ value: type, label: type }))}
                  value={item.loadType || DEFAULT_LOAD_TYPE}
                  onChange={(value) => updateCargoItem(index, 'loadType', value || DEFAULT_LOAD_TYPE)}
                  placeholder="Search or select load type"
                  allowOther={false}
                />
              </div>
              <div>
                <Label>Commodity</Label>
                <SearchableSelect
                  options={TRANSPORT_COMMODITY_OPTIONS.map((comm) => ({ value: comm, label: comm }))}
                  value={item.commodity || ''}
                  onChange={(value) => {
                    updateCargoItem(index, 'commodity', value || '');
                    if (index === 0 && value) clearValidationError?.(['cargoDetails', 'cargoDetails.0.commodity']);
                  }}
                  placeholder="Search or select commodity"
                  allowOther={false}
                  error={index === 0 && !!validationErrors['cargoDetails.0.commodity']}
                />
                {index === 0 && validationErrors['cargoDetails.0.commodity'] && <p className="mt-1 text-sm text-red-600">{validationErrors['cargoDetails.0.commodity']}</p>}
              </div>
              <div>
                <Label>Total Weight (kg)</Label>
                <Input
                  type="number"
                  value={item.cargoWeight ?? ''}
                  onChange={(e) => updateCargoItem(index, 'cargoWeight', e.target.value)}
                  onInput={(e) => {
                    if (index === 0 && e.target.value) clearValidationError?.(['cargoDetails', 'cargoDetails.0.cargoWeight']);
                  }}
                  className={index === 0 && validationErrors['cargoDetails.0.cargoWeight'] ? 'border-red-500' : ''}
                  required
                />
                {index === 0 && validationErrors['cargoDetails.0.cargoWeight'] && <p className="mt-1 text-sm text-red-600">{validationErrors['cargoDetails.0.cargoWeight']}</p>}
              </div>
              <div>
                <Label>Volume (m³)</Label>
                <Input
                  type="number"
                  value={item.cargoVolume ?? ''}
                  onChange={(e) => updateCargoItem(index, 'cargoVolume', e.target.value)}
                  onInput={(e) => {
                    if (index === 0 && e.target.value) clearValidationError?.('cargoDetails.0.cargoVolume');
                  }}
                  className={index === 0 && validationErrors['cargoDetails.0.cargoVolume'] ? 'border-red-500' : ''}
                  required
                />
                {index === 0 && validationErrors['cargoDetails.0.cargoVolume'] && <p className="mt-1 text-sm text-red-600">{validationErrors['cargoDetails.0.cargoVolume']}</p>}
              </div>
            </div>
            <div className="mt-4">
              <Label>Dimensions per Piece</Label>
              <div className="flex flex-wrap items-end gap-2 mt-1">
                <SearchableSelect
                  options={DIMENSION_UNIT_OPTIONS}
                  className="w-24 shrink-0"
                  value={item.dimensions?.unit ?? 'cm'}
                  onChange={(value) => updateCargoItem(index, 'dimensions.unit', value || 'cm')}
                  placeholder="Unit"
                  allowOther={false}
                />
                <Input
                  type="number"
                  className="flex-1 min-w-0"
                  value={item.dimensions?.length ?? ''}
                  onChange={(e) => updateCargoItem(index, 'dimensions.length', e.target.value)}
                  onInput={(e) => {
                    if (index === 0 && e.target.value) clearValidationError?.('cargoDetails.0.dimensions.length');
                  }}
                  className={`flex-1 min-w-0 ${index === 0 && validationErrors['cargoDetails.0.dimensions.length'] ? 'border-red-500' : ''}`}
                  placeholder="Length"
                  required
                />
                {index === 0 && validationErrors['cargoDetails.0.dimensions.length'] && <p className="mt-1 text-sm text-red-600 basis-full">{validationErrors['cargoDetails.0.dimensions.length']}</p>}
                <Input
                  type="number"
                  className={`flex-1 min-w-0 ${index === 0 && validationErrors['cargoDetails.0.dimensions.width'] ? 'border-red-500' : ''}`}
                  value={item.dimensions?.width ?? ''}
                  onChange={(e) => updateCargoItem(index, 'dimensions.width', e.target.value)}
                  onInput={(e) => {
                    if (index === 0 && e.target.value) clearValidationError?.('cargoDetails.0.dimensions.width');
                  }}
                  placeholder="Width"
                  required
                />
                {index === 0 && validationErrors['cargoDetails.0.dimensions.width'] && <p className="mt-1 text-sm text-red-600 basis-full">{validationErrors['cargoDetails.0.dimensions.width']}</p>}
                <Input
                  type="number"
                  className={`flex-1 min-w-0 ${index === 0 && validationErrors['cargoDetails.0.dimensions.height'] ? 'border-red-500' : ''}`}
                  value={item.dimensions?.height ?? ''}
                  onChange={(e) => updateCargoItem(index, 'dimensions.height', e.target.value)}
                  onInput={(e) => {
                    if (index === 0 && e.target.value) clearValidationError?.('cargoDetails.0.dimensions.height');
                  }}
                  placeholder="Height"
                  required
                />
                {index === 0 && validationErrors['cargoDetails.0.dimensions.height'] && <p className="mt-1 text-sm text-red-600 basis-full">{validationErrors['cargoDetails.0.dimensions.height']}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Customs Clearance */}
      {formData.requiresCustomsClearance && (
        <div className="border-t pt-4">
          <h3 className="font-semibold text-lg mb-4">Customs Clearance Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Export Country</Label>
              <Input
                value={formData.customsDetails.exportCountry}
                onChange={(e) => setFormData({ ...formData, customsDetails: { ...formData.customsDetails, exportCountry: e.target.value } })}
                required={formData.requiresCustomsClearance}
              />
            </div>
            <div>
              <Label>Import Country</Label>
              <Input
                value={formData.customsDetails.importCountry}
                onChange={(e) => setFormData({ ...formData, customsDetails: { ...formData.customsDetails, importCountry: e.target.value } })}
                required={formData.requiresCustomsClearance}
              />
            </div>
            <div>
              <Label>HS Code</Label>
              <Input
                value={formData.customsDetails.hsCode}
                onChange={(e) => setFormData({ ...formData, customsDetails: { ...formData.customsDetails, hsCode: e.target.value } })}
                placeholder="6-10 digit code"
              />
            </div>
            <div>
              <Label>Invoice Value (USD)</Label>
              <Input
                type="number"
                value={formData.customsDetails.customsValue}
                onChange={(e) => setFormData({ ...formData, customsDetails: { ...formData.customsDetails, customsValue: e.target.value } })}
              />
            </div>
          </div>
        </div>
      )}

      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Insurance</h3>
        <div className="space-y-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.insuranceRequired}
              onChange={(e) => setFormData({
                ...formData,
                insuranceRequired: e.target.checked,
                insuranceValue: e.target.checked ? formData.insuranceValue : ''
              })}
              className="rounded"
            />
            <span className="text-sm font-medium">Insurance Required</span>
          </label>

          {formData.insuranceRequired && (
            <div className="max-w-md">
              <Label>Invoice Value (USD)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.insuranceValue}
                onChange={(e) => setFormData({ ...formData, insuranceValue: e.target.value })}
                placeholder="Enter insured invoice value"
                required={formData.insuranceRequired}
              />
            </div>
          )}
        </div>
      </div>

    </form>
  );
};

export default TransportForm;
